// ─── Types ────────────────────────────────────────────────────────────────────

/** A single candidate solution. */
export interface Individual<G> {
    /** The gene sequence representing a solution. */
    genes: G[];
    /** Fitness score. Higher values = more fit (by convention). */
    fitness: number;
}

export interface GARunOptions<G> {
    /** Initial population (already evaluated). */
    population: Individual<G>[];
    /**
     * Returns a fitness score for a gene sequence.
     * Higher = more fit. Called every generation.
     */
    fitnessFn: (genes: G[]) => number;
    /** Number of generations to run. */
    generations: number;
    /** Probability [0, 1] that each gene mutates. Default: 0.01 */
    mutationRate?: number;
    /**
     * Called when a gene should be replaced by a random one.
     * Required for the `random` mutation strategy.
     */
    geneFactory?: () => G;
    /** Selection strategy. Default: `'tournament'` */
    selection?: SelectionStrategy;
    /** Tournament size when using tournament selection. Default: 3 */
    tournamentSize?: number;
    /** Crossover strategy. Default: `'single-point'` */
    crossover?: CrossoverStrategy;
    /**
     * Fraction of the population that advances to the next generation unchanged.
     * Default: 0.1  (10% elitism)
     */
    elitismRate?: number;
    /**
     * Called after every generation with the current best individual and
     * generation index (0-based). Useful for logging progress.
     */
    onGeneration?: (best: Individual<G>, generation: number) => void;
}

export interface GAResult<G> {
    /** Best individual found across all generations. */
    best: Individual<G>;
    /** Final population (sorted best-first). */
    finalPopulation: Individual<G>[];
    /** Best fitness score of each generation. */
    history: number[];
}

export type SelectionStrategy = 'tournament' | 'roulette' | 'rank';
export type CrossoverStrategy = 'single-point' | 'two-point' | 'uniform';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cloneIndividual<G>(ind: Individual<G>): Individual<G> {
    return { genes: [...ind.genes], fitness: ind.fitness };
}

function randInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min)) + min;
}

// ─── Population ──────────────────────────────────────────────────────────────

/**
 * Creates a population of random individuals.
 *
 * @param size Number of individuals.
 * @param length Number of genes per individual.
 * @param geneFactory Returns a single random gene value.
 *
 * @example
 * // Binary-encoded population
 * const pop = GA.createPopulation(50, 20, () => Math.random() < 0.5 ? 0 : 1);
 *
 * @example
 * // Real-valued population in [-5, 5]
 * const pop = GA.createPopulation(100, 10, () => Math.random() * 10 - 5);
 */
function createPopulation<G>(
    size: number,
    length: number,
    geneFactory: () => G,
): Individual<G>[] {
    return Array.from({ length: size }, () => ({
        genes: Array.from({ length }, geneFactory),
        fitness: 0,
    }));
}

// ─── Evaluation ──────────────────────────────────────────────────────────────

/**
 * Evaluates and assigns fitness to every individual in the population.
 * Returns a new array (does not mutate the original).
 *
 * @example
 * const evaluated = GA.evaluate(pop, (genes) => genes.filter(Boolean).length);
 */
function evaluate<G>(
    population: Individual<G>[],
    fitnessFn: (genes: G[]) => number,
): Individual<G>[] {
    return population.map((ind) => ({ ...ind, fitness: fitnessFn(ind.genes) }));
}

// ─── Sorting / Statistics ─────────────────────────────────────────────────────

/**
 * Sorts the population by fitness.
 * @param order `'desc'` (default) = best first, `'asc'` = worst first.
 */
function sortPopulation<G>(
    population: Individual<G>[],
    order: 'asc' | 'desc' = 'desc',
): Individual<G>[] {
    const sorted = [...population];
    sorted.sort((a, b) => order === 'desc' ? b.fitness - a.fitness : a.fitness - b.fitness);
    return sorted;
}

/**
 * Returns the top-n fittest individuals (best-first).
 */
function best<G>(population: Individual<G>[], n: number = 1): Individual<G>[] {
    return sortPopulation(population).slice(0, n);
}

// ─── Selection ───────────────────────────────────────────────────────────────

/**
 * Tournament selection: picks `tournamentSize` random individuals and returns
 * the fittest among them.
 */
function tournamentSelect<G>(
    population: Individual<G>[],
    tournamentSize: number = 3,
): Individual<G> {
    let winner = population[randInt(0, population.length)];
    for (let i = 1; i < tournamentSize; i++) {
        const contender = population[randInt(0, population.length)];
        if (contender.fitness > winner.fitness) winner = contender;
    }
    return winner;
}

/**
 * Fitness-proportionate (roulette wheel) selection.
 * Requires all fitness values to be non-negative.
 */
function rouletteSelect<G>(population: Individual<G>[]): Individual<G> {
    const total = population.reduce((s, ind) => s + Math.max(0, ind.fitness), 0);
    if (total === 0) return population[randInt(0, population.length)];
    let r = Math.random() * total;
    for (const ind of population) {
        r -= Math.max(0, ind.fitness);
        if (r <= 0) return ind;
    }
    return population[population.length - 1];
}

/**
 * Rank-based selection: selection probability is proportional to rank, not
 * raw fitness. Helps avoid premature convergence.
 */
function rankSelect<G>(population: Individual<G>[]): Individual<G> {
    const sorted = sortPopulation(population, 'asc'); // worst first → rank 1
    const n = sorted.length;
    const totalRank = (n * (n + 1)) / 2;
    let r = Math.random() * totalRank;
    for (let i = 0; i < sorted.length; i++) {
        r -= (i + 1);
        if (r <= 0) return sorted[i];
    }
    return sorted[sorted.length - 1];
}

function select<G>(
    population: Individual<G>[],
    strategy: SelectionStrategy,
    tournamentSize: number,
): Individual<G> {
    switch (strategy) {
        case 'roulette': return rouletteSelect(population);
        case 'rank': return rankSelect(population);
        default: return tournamentSelect(population, tournamentSize);
    }
}

// ─── Crossover ───────────────────────────────────────────────────────────────

/**
 * Single-point crossover: splits both parents at a random point and swaps tails.
 */
function singlePointCrossover<G>(
    p1: Individual<G>,
    p2: Individual<G>,
): [Individual<G>, Individual<G>] {
    const len = p1.genes.length;
    const cut = randInt(1, len);
    return [
        { genes: [...p1.genes.slice(0, cut), ...p2.genes.slice(cut)], fitness: 0 },
        { genes: [...p2.genes.slice(0, cut), ...p1.genes.slice(cut)], fitness: 0 },
    ];
}

/**
 * Two-point crossover: swaps the segment between two random cut points.
 */
function twoPointCrossover<G>(
    p1: Individual<G>,
    p2: Individual<G>,
): [Individual<G>, Individual<G>] {
    const len = p1.genes.length;
    let a = randInt(0, len);
    let b = randInt(0, len);
    if (a > b) [a, b] = [b, a];
    const c1 = [
        ...p1.genes.slice(0, a),
        ...p2.genes.slice(a, b),
        ...p1.genes.slice(b),
    ];
    const c2 = [
        ...p2.genes.slice(0, a),
        ...p1.genes.slice(a, b),
        ...p2.genes.slice(b),
    ];
    return [
        { genes: c1, fitness: 0 },
        { genes: c2, fitness: 0 },
    ];
}

/**
 * Uniform crossover: each gene is independently taken from either parent
 * with equal probability.
 * @param mixRate Probability of taking each gene from parent2 (default 0.5).
 */
function uniformCrossover<G>(
    p1: Individual<G>,
    p2: Individual<G>,
    mixRate: number = 0.5,
): [Individual<G>, Individual<G>] {
    const c1: G[] = [];
    const c2: G[] = [];
    for (let i = 0; i < p1.genes.length; i++) {
        if (Math.random() < mixRate) {
            c1.push(p2.genes[i]);
            c2.push(p1.genes[i]);
        } else {
            c1.push(p1.genes[i]);
            c2.push(p2.genes[i]);
        }
    }
    return [
        { genes: c1, fitness: 0 },
        { genes: c2, fitness: 0 },
    ];
}

function crossover<G>(
    p1: Individual<G>,
    p2: Individual<G>,
    strategy: CrossoverStrategy,
): [Individual<G>, Individual<G>] {
    switch (strategy) {
        case 'two-point': return twoPointCrossover(p1, p2);
        case 'uniform': return uniformCrossover(p1, p2);
        default: return singlePointCrossover(p1, p2);
    }
}

// ─── Mutation ────────────────────────────────────────────────────────────────

/**
 * Bit-flip mutation for boolean/binary-encoded individuals.
 * Each gene is flipped with probability `rate`.
 */
function bitFlipMutate(
    individual: Individual<number | boolean>,
    rate: number,
): Individual<number | boolean> {
    const genes = individual.genes.map((g) =>
        Math.random() < rate ? (g ? 0 : 1) : g,
    );
    return { genes, fitness: 0 };
}

/**
 * Swap mutation: randomly selects two positions and swaps them.
 * Applied once if `Math.random() < rate`.
 * Useful for permutation-encoded problems (TSP, scheduling).
 */
function swapMutate<G>(
    individual: Individual<G>,
    rate: number,
): Individual<G> {
    if (Math.random() >= rate) return cloneIndividual(individual);
    const genes = [...individual.genes];
    const i = randInt(0, genes.length);
    let j = randInt(0, genes.length - 1);
    if (j >= i) j++;
    [genes[i], genes[j]] = [genes[j], genes[i]];
    return { genes, fitness: 0 };
}

/**
 * Inversion mutation: reverses a random sub-sequence of genes.
 * Applied once if `Math.random() < rate`.
 */
function inversionMutate<G>(
    individual: Individual<G>,
    rate: number,
): Individual<G> {
    if (Math.random() >= rate) return cloneIndividual(individual);
    const genes = [...individual.genes];
    let a = randInt(0, genes.length);
    let b = randInt(0, genes.length);
    if (a > b) [a, b] = [b, a];
    genes.splice(a, b - a + 1, ...genes.slice(a, b + 1).reverse());
    return { genes, fitness: 0 };
}

/**
 * Random-reset mutation: replaces each gene with a new random value with
 * probability `rate`. Works for any encoding.
 * @param geneFactory Returns a random gene value.
 */
function randomResetMutate<G>(
    individual: Individual<G>,
    rate: number,
    geneFactory: () => G,
): Individual<G> {
    const genes = individual.genes.map((g) =>
        Math.random() < rate ? geneFactory() : g,
    );
    return { genes, fitness: 0 };
}

// ─── Evolution Loop ───────────────────────────────────────────────────────────

/**
 * Runs a full genetic algorithm evolution loop.
 *
 * @example
 * // Maximise the number of 1-bits in a 20-gene binary chromosome
 * const pop = GA.evaluate(
 *   GA.createPopulation(50, 20, () => Math.round(Math.random())),
 *   (genes) => genes.reduce((a, b) => a + b, 0),
 * );
 *
 * const result = GA.run({
 *   population: pop,
 *   fitnessFn: (genes) => genes.reduce((a, b) => a + b, 0),
 *   generations: 100,
 *   mutationRate: 0.02,
 *   geneFactory: () => Math.round(Math.random()),
 * });
 *
 * console.log(result.best.genes, result.best.fitness);
 */
function run<G>(options: GARunOptions<G>): GAResult<G> {
    const {
        fitnessFn,
        generations,
        mutationRate = 0.01,
        geneFactory,
        selection: selectionStrategy = 'tournament',
        tournamentSize = 3,
        crossover: crossoverStrategy = 'single-point',
        elitismRate = 0.1,
        onGeneration,
    } = options;

    let population = [...options.population];
    const history: number[] = [];
    let overallBest: Individual<G> = sortPopulation(population)[0];

    for (let gen = 0; gen < generations; gen++) {
        const popSize = population.length;
        const eliteCount = Math.max(1, Math.round(popSize * elitismRate));

        // Elitism: carry the best individuals unchanged
        const elites = sortPopulation(population).slice(0, eliteCount).map(cloneIndividual);

        // Fill the rest of the new population via selection + crossover + mutation
        const offspring: Individual<G>[] = [];
        while (offspring.length < popSize - eliteCount) {
            const p1 = select(population, selectionStrategy, tournamentSize);
            const p2 = select(population, selectionStrategy, tournamentSize);
            const [c1, c2] = crossover(p1, p2, crossoverStrategy);

            for (const child of [c1, c2]) {
                let mutated: Individual<G>;
                if (geneFactory) {
                    mutated = randomResetMutate(child, mutationRate, geneFactory);
                } else {
                    mutated = swapMutate(child, mutationRate);
                }
                offspring.push(mutated);
                if (offspring.length >= popSize - eliteCount) break;
            }
        }

        // Evaluate new individuals, keep elites as-is
        const evaluated = evaluate(offspring, fitnessFn);
        population = [...elites, ...evaluated];

        const genBest = sortPopulation(population)[0];
        history.push(genBest.fitness);
        if (genBest.fitness > overallBest.fitness) overallBest = cloneIndividual(genBest);

        onGeneration?.(genBest, gen);
    }

    return {
        best: overallBest,
        finalPopulation: sortPopulation(population),
        history,
    };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const GA = {
    /** Create a random population of given size and chromosome length. */
    createPopulation,
    /** Evaluate and assign fitness scores to all individuals. Returns new array. */
    evaluate,
    /** Sort a population by fitness (`'desc'` = best first, default). */
    sort: sortPopulation,
    /** Return the top-n fittest individuals. */
    best,

    selection: {
        /** Tournament selection — pick the best among `tournamentSize` random candidates. */
        tournament: tournamentSelect,
        /** Fitness-proportionate (roulette wheel) selection. */
        roulette: rouletteSelect,
        /** Rank-based selection — selection pressure without raw-fitness dominance. */
        rank: rankSelect,
    },

    crossover: {
        /** Split at one random cut point and swap tails. */
        singlePoint: singlePointCrossover,
        /** Swap the segment between two random cut points. */
        twoPoint: twoPointCrossover,
        /**
         * Each gene is independently drawn from either parent.
         * @param mixRate Probability of taking from parent2 (default 0.5).
         */
        uniform: uniformCrossover,
    },

    mutation: {
        /**
         * Flip each binary gene with probability `rate`.
         * Designed for `0 | 1` or `boolean` encodings.
         */
        bitFlip: bitFlipMutate,
        /**
         * Swap two random positions with probability `rate`.
         * Best for permutation encodings (TSP, scheduling).
         */
        swap: swapMutate,
        /**
         * Reverse a random sub-sequence with probability `rate`.
         * Good complement to crossover for permutation problems.
         */
        inversion: inversionMutate,
        /**
         * Replace each gene with a new random value with probability `rate`.
         * Works for any encoding; requires a `geneFactory`.
         */
        randomReset: randomResetMutate,
    },

    /**
     * Run a full evolution loop. Handles selection, crossover, mutation,
     * elitism, and fitness evaluation each generation.
     */
    run,
};
